import produce from 'immer'
import * as ActionTypes from 'actions/ActionTypes'
import { VIEW_FORM_STATE, BEHAVIOUR_FORM_STATE, RENDER_FORM_STATE, ViewSettings, BehaviourSettings, RenderSettings } from 'config/constants/editorSettings'
import { EDITOR_MAIN_SETTINGS, SAVE_SETTINGS_MODE, SNAPSHOT_SAVE_MODE } from 'config/constants/mainView'
import { DROP_TARGET_TYPE } from 'enums'
import PROFILE_CONFIG from 'features/desktop-activation-feature/config'
import { translations } from 'translations/translations'
import { getFileTypeByAsset } from '~/helpers/assets/getFileTypeByAsset'
import * as PlayerVolume from '~/cookieServices/PlayerVolume'
import { getDefaultChapterFromStorage, setChapterAtStorage } from '~/general'

function setEditorSettingsFromUrlParams() {
  const search = new URLSearchParams(window.location.search)
  const categories = [
    { category: EDITOR_MAIN_SETTINGS.VIEW, keys: ViewSettings },
    { category: EDITOR_MAIN_SETTINGS.BEHAVIOUR, keys: BehaviourSettings },
    { category: EDITOR_MAIN_SETTINGS.RENDER, keys: RenderSettings },
  ]

  for (const { category, keys } of categories) {
    const stored = JSON.parse(localStorage.getItem(category) || '{}')
    let changed = false
    for (const key of Object.values(keys)) {
      if (!search.has(key)) continue
      const value = search.get(key)
      if (value === '') {
        if (key in stored) {
          delete stored[key]
          changed = true
        }
      } else if (value === '0' || value === '1') {
        stored[key] = value === '1' ? 1 : 0
        changed = true
      }
    }
    if (changed) {
      localStorage.setItem(category, JSON.stringify(stored))
    }
  }

  const stringSettings = [ 'referrer', 'renderCompleteRedirectUrl', 'outputS3KeyPrefix' ]
  for (const key of stringSettings) {
    if (!search.has(key)) continue
    const value = search.get(key)
    if (value === '') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, value)
    }
  }

  if (search.has('snapshotSaveMode')) {
    const snapshotSaveMode = search.get('snapshotSaveMode')
    if (snapshotSaveMode === '') {
      localStorage.removeItem('snapshotSaveMode')
    } else if (snapshotSaveMode === SNAPSHOT_SAVE_MODE.DOWNLOADS
      || snapshotSaveMode === SNAPSHOT_SAVE_MODE.UPLOAD) {
      localStorage.setItem('snapshotSaveMode', snapshotSaveMode)
    }
  }

  const langParam = search.get('lang')
  if (langParam !== null) {
    const stored = JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.VIEW) || '{}')
    if (!langParam) {
      delete stored.lang
    } else if (langParam in translations) {
      stored.lang = langParam
    }
    localStorage.setItem(EDITOR_MAIN_SETTINGS.VIEW, JSON.stringify(stored))
  }

  if (search.get('resetSettings') === '1') {
    localStorage.removeItem(EDITOR_MAIN_SETTINGS.RENDER)
    localStorage.removeItem(EDITOR_MAIN_SETTINGS.BEHAVIOUR)
    localStorage.removeItem(EDITOR_MAIN_SETTINGS.VIEW)
    localStorage.removeItem('referrer')
    localStorage.removeItem('renderCompleteRedirectUrl')
    localStorage.removeItem('outputS3KeyPrefix')
    localStorage.removeItem('snapshotSaveMode')
  }
}

setEditorSettingsFromUrlParams()

/**
 * @typedef {object} RenderSettings
 * @property {number} skipEmptySpace
 */

/**
 * @typedef {object} BehaviourSettings
 * @property {number} setTimecodeFormatBasedOnMedia
 */

/**
 * @typedef {object} ViewSettings
 * @property {boolean} showProjectMedia
 * @property {boolean} showMyMedia
 * @property {boolean} showPreviewSplitButton
 * @property {boolean} showPreviewSwitchButtons
 * @property {boolean} showInOutPointsTimeline
 * @property {boolean} showInOutPointsSource
 * @property {boolean} showDropPlaceHolderTimeline
 */

/**
 * @typedef {object} MainView
 * @property {MenuItems} menuItem
 * @property {boolean} showSaveSettings
 * @property {null | string} showAssetSettings
 * @property { 'default' | 'new_publish' } saveSettingsMode
 * @property {boolean} showNabletHldSettings
 * @property {boolean} showTranscribeSettings
 * @property {boolean | string} showCustomizeView
 * @property {boolean} showImportFromUrl
 * @property {boolean} showAmazonImport
 * @property {boolean} showEditorSettings
 * @property {boolean} showReferenceFileChoice
 * @property {boolean} showDeleteMediaFilesForm
 * @property {boolean} showLogActions
 * @property {boolean} showQRCode
 * @property {boolean} showAuthTokenMessage
 * @property {boolean} showInstallComponentMessage
 * @property {Array} referenceFilesData
 * @property {boolean} isLocalFileDragging
 * @property {number} playerVolume
 * @property {{ x: number, y: number }} activeAssetRotation
 * @property {boolean} isLoading
 * @property {boolean} isOffline
 * @property {{ mediaRecordingSettings: 'audio' | 'video' | null }} showFeatures
 * @property {boolean} offlineRestoration
 * @property {RenderSettings} editorRenderSettings
 * @property {BehaviourSettings} editorBehaviourSettings
 * @property {ViewSettings} editorViewSettings
 * @property {string} edition
 * @property {string | null} activeDragElement
 * @property {string | null} errorMessage
 * @property {DOMRect | object} dragElementBounds
 * @property {'root' | 'timeline' | 'sourceMedia'} dndDropTarget
 */

/**
 * @type {MainView}
 */
const initialState = {
  menuItem: getDefaultChapterFromStorage(),
  showSaveSettings: false,
  saveSettingsMode: SAVE_SETTINGS_MODE.DEFAULT,
  showNabletHldSettings: false,
  showTranscribeSettings: false,
  showAssetSettings: false,
  showCustomizeView: false,
  showImportFromUrl: false,
  showAmazonImport: false,
  showEditorSettings: false,
  showReferenceFileChoice: false,
  showDeleteMediaFilesForm: false,
  showLogActions: false,
  showQRCode: false,
  showAuthTokenMessage: false,
  showInstallComponentMessage: false,
  referenceFilesData: [],
  isLocalFileDragging: false,
  playerVolume: PlayerVolume.get(),
  // TODO: new rotation expect correct x and y coords, we can get them only from canvas
  // refactor this to another state slice or use explicit action listening instead of state
  activeAssetRotation: undefined,
  isLoading: false,
  isOffline: false,
  offlineRestoration: false,
  showFeatures: { mediaRecordingSettings: null },
  batchImportFiles: { visible: false, progress: 0, taskId: null, status: '', error: '', fileIds: [], mediaPool: null },
  activeDragElement: null,
  dragElementBounds: {},
  dndDropTarget: DROP_TARGET_TYPE.ROOT,
  errorMessage: null,
  pendingSelectedAssetId: null,
  edition: localStorage.getItem(PROFILE_CONFIG.LOCAL_STORAGE_EDITION_VAR), // desktop only
  editorRenderSettings: {
    ...RENDER_FORM_STATE,
    ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.RENDER) || '{}'),
  },
  editorBehaviourSettings: {
    ...BEHAVIOUR_FORM_STATE,
    ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.BEHAVIOUR) || '{}'),
  },
  editorViewSettings: {
    ...VIEW_FORM_STATE,
    ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.VIEW) || '{}'),
  },
}

const mainView = (state, action) => {
  // eslint-disable-next-line default-case
  switch (action.type) {
    case ActionTypes.SET_ERROR_MESSAGE: {
      const { message } = action.payload
      state.errorMessage = message
    }
      break
    case ActionTypes.OPEN_CHAPTER: {
      state.showAssetSettings = null
      const { menuItem, force } = action.payload
      const chapter = (action.payload.menuItem === state.menuItem && !force) ? '' : menuItem
      openChapter(state, chapter)
      break
    }

    case ActionTypes.SET_IS_OFFLINE: {
      const { isOffline } = action.payload
      state.isOffline = isOffline
      break
    }

    case ActionTypes.ASSET_EDIT_STARTED: {
      const { asset } = action.payload
      // openChapter(state, getMenuChapterByAsset(asset))
      state.showAssetSettings = getFileTypeByAsset(asset)
      break
    }

    case ActionTypes.ASSET_EDIT_CANCELED: {
      // openChapter(state, getDefaultChapterFromStorage())
      state.showAssetSettings = null
      break
    }

    case ActionTypes.SAVE_SETTINGS_VISIBILITY_CHANGED:
      state.showSaveSettings = action.payload.showSettings
      break
    case ActionTypes.SHOW_DELETE_MEDIA_FILES_FORM: {
      const { visibility } = action.payload
      state.showDeleteMediaFilesForm = visibility
    }
      break
    case ActionTypes.NABLET_HLD_SETTINGS_VISIBILITY_CHANGED:
      state.showNabletHldSettings = action.payload.showSettings
      break
    case ActionTypes.TRANSCRIBE_SETTINGS_VISIBILITY_CHANGED:
      state.showTranscribeSettings = action.payload.showSettings
      break
    case ActionTypes.CUSTOMIZE_VIEW_VISIBILITY_CHANGED:
      state.showCustomizeView = action.payload
      break

    case ActionTypes.EDITOR_MAIN_SETTINGS:
      state.showEditorSettings = action.payload
      break
    case ActionTypes.SHOW_REFERENCE_FILE_CHOICE_WINDOW:
      state.showReferenceFileChoice = action.payload.visibility
      break
    case ActionTypes.LOG_ACTIONS:
      state.showLogActions = action.payload
      break
    case ActionTypes.INSTALL_COMPONENT_MESSAGE_VISIBILITY_CHANGED:
      state.showInstallComponentMessage = action.payload
      break
    case ActionTypes.REFERENCE_FILE_CHOICE:
      state.referenceFilesData = action.payload.referenceFilesData
      break
    case ActionTypes.IMPORT_FROM_URL_VISIBILITY_CHANGED:
      state.showImportFromUrl = action.payload.showImportFromUrl
      break

    case ActionTypes.SET_PENDING_SELECTED_ASSET_ID:
      state.pendingSelectedAssetId = action.payload.pendingSelectedAssetId
      break

    case ActionTypes.AMAZON_IMPORT_VISIBILITY_CHANGED:
      state.showAmazonImport = action.payload.showAmazonImport
      break

    case ActionTypes.LOCAL_FILE_DRAGGING:
      state.isLocalFileDragging = action.payload.isDragging
      break
    case ActionTypes.ACTIVE_ASSET_ROTATION:
      state.activeAssetRotation = action.payload.activeAssetRotation
      break
    case ActionTypes.SET_PLAYER_VOLUME: {
      const { playerVolume, persist } = action.payload
      state.playerVolume = playerVolume
      if (persist) {
        PlayerVolume.set(playerVolume)
      }
      break
    }
    case ActionTypes.MEDIA_RECORDING_INIT: {
      state.showFeatures.mediaRecordingSettings = action.payload
      break
    }
    case ActionTypes.MEDIA_RECORDING_CANCELED: {
      state.showFeatures.mediaRecordingSettings = null
      break
    }
    case ActionTypes.CLOSE_FEATURES: {
      state.showCustomizeView = false
      Object.keys(state.showFeatures).forEach(key => { state.showFeatures[key] = null })
      break
    }
    case ActionTypes.RESET_MAIN_VIEW: {
      const correctInitialState = {
        ...initialState,
        menuItem: getDefaultChapterFromStorage(),
        editorRenderSettings: {
          ...RENDER_FORM_STATE,
          ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.RENDER) || '{}'),
        },
        editorBehaviourSettings: {
          ...BEHAVIOUR_FORM_STATE,
          ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.BEHAVIOUR) || '{}'),
        },
        editorViewSettings: {
          ...VIEW_FORM_STATE,
          ...JSON.parse(localStorage.getItem(EDITOR_MAIN_SETTINGS.VIEW) || '{}'),
        },
      }
      Object.assign(state, correctInitialState)
      break
    }
    case ActionTypes.OFFLINE_RESTORATION_START: {
      state.offlineRestoration = true
      break
    }
    case ActionTypes.OFFLINE_RESTORATION_END: {
      state.offlineRestoration = false
      break
    }
    case ActionTypes.SET_DND_DROP_TARGET:
      state.dndDropTarget = action.payload
      break
    case ActionTypes.SET_ACTIVE_DRAG_ELEMENT: {
      const { element } = action.payload
      state.activeDragElement = element
    }
      break
    case ActionTypes.SET_DRAG_ELEMENT_BOUNDS: {
      const { bounds } = action.payload
      state.dragElementBounds = bounds ?? {}
    }
      break
    case ActionTypes.SET_SAVE_SETTINGS_MODE: {
      const { mode } = action.payload
      state.saveSettingsMode = mode
    }
      break
    case ActionTypes.SET_EDITOR_SETTINGS: {
      const { chapter, params } = action.payload
      const stateKey = {
        [EDITOR_MAIN_SETTINGS.RENDER]: 'editorRenderSettings',
        [EDITOR_MAIN_SETTINGS.BEHAVIOUR]: 'editorBehaviourSettings',
        [EDITOR_MAIN_SETTINGS.VIEW]: 'editorViewSettings',
      }[chapter]
      state[stateKey] = params
      localStorage.setItem(chapter, JSON.stringify(params))
    }
      break
    case ActionTypes.SHOW_IMPORT_FILES_DIALOG: {
      const { visible } = action.payload
      state.batchImportFiles = { ...state.batchImportFiles, visible }
    }
      break
    case ActionTypes.UPDATE_IMPORT_FILES_DATA: {
      const data = action.payload ?? {}
      state.batchImportFiles = { ...state.batchImportFiles, ...data }
    }
      break
    case ActionTypes.RESET_IMPORT_FILES_DATA:
      state.batchImportFiles = { visible: false, progress: 0, taskId: null, status: '', error: '', fileIds: [], mediaPool: null }
      break
    case ActionTypes.SHOW_QR_CODE:
      state.showQRCode = !!action.payload?.visible
      break
    case ActionTypes.SET_AUTH_TOKEN_MISSED:
      state.showAuthTokenMessage = action.payload
  }
}

export default produce(mainView, initialState)

// ---

function openChapter(state, chapter) {
  state.menuItem = chapter
  setChapterAtStorage(chapter)
}
